import React, { useEffect, useMemo, useState } from "react";
import { Users as UsersIcon, Plus, Circle, Pencil, Trash2, History, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select } from "../lib/ui";
import { useToast } from "../lib/toast";
import { ROLE_LABELS } from "../lib/constants";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { permLabel, moduleLabel } from "../lib/permissions";

const EMPTY = { id: null, name: "", username: "", email: "", password: "", role: "BRANCH_STAFF", branch_id: "", scope_type: "all", permissions: [], template: "", auth_provider: "", approving: false };

export default function UsersPage() {
  const toast = useToast();
  const { t, lang } = useT();
  const { user: me, hasPermission, refreshPermissions } = useAuth();
  const [users, setUsers] = useState(null);
  const [branches, setBranches] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null); // { user, logs }
  const [tab, setTab] = useState("active"); // active | pending

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => {
    load();
    api.get("/branches").then(({ data }) => setBranches(data));
    api.get("/permissions/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  if (!users || !catalog) return <PageLoader />;

  const grantable = new Set(catalog.grantable || []);
  const assignable = catalog.assignable_roles || [];
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const canManage = (u) => catalog.can_manage_super_admin || u.role !== "SUPER_ADMIN";
  const pendingUsers = users.filter((u) => u.status === "pending");
  const activeUsers = users.filter((u) => u.status !== "pending");

  const openCreate = () => { setForm({ ...EMPTY, role: assignable.includes("BRANCH_STAFF") ? "BRANCH_STAFF" : assignable[0] }); setOpen(true); };
  const openEdit = (u) => {
    setForm({
      id: u.id, name: u.name || "", username: u.username || "", email: u.email || "", password: "",
      role: u.role, branch_id: u.branch_id || "",
      scope_type: u.scope_type || u.effective_scope_type || "all",
      permissions: Array.isArray(u.permissions) ? [...u.permissions] : [...(u.effective_permissions || [])],
      template: u.template || "",
      auth_provider: u.auth_provider || "", approving: false,
    });
    setOpen(true);
  };

  // Approve a PENDING Google user: assign Role + Branch + activate in one step.
  const openApprove = (u) => {
    setForm({
      id: u.id, name: u.name || "", username: u.username || "",
      email: u.email || u.google_email || "", password: "",
      role: assignable.includes("BRANCH_STAFF") ? "BRANCH_STAFF" : assignable[0],
      branch_id: "", scope_type: "all", permissions: [], template: "",
      auth_provider: u.auth_provider || "google", approving: true,
    });
    setOpen(true);
  };

  const applyTemplate = (id) => {
    const tpl = (catalog.templates || []).find((x) => x.id === id);
    if (!tpl) { setForm((f) => ({ ...f, template: "" })); return; }
    setForm((f) => ({
      ...f, template: id, role: tpl.role,
      permissions: (tpl.permissions || []).filter((p) => grantable.has(p)),
      scope_type: tpl.role === "BRANCH_STAFF" ? "branch" : "all",
    }));
  };

  const togglePerm = (p) => {
    if (!grantable.has(p)) return;
    setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));
  };
  const toggleModule = (mod) => {
    const keys = mod.permissions.filter((p) => grantable.has(p));
    const allOn = keys.every((p) => form.permissions.includes(p));
    setForm((f) => ({
      ...f,
      permissions: allOn ? f.permissions.filter((p) => !keys.includes(p)) : [...new Set([...f.permissions, ...keys])],
    }));
  };
  const selectAll = () => setForm((f) => ({ ...f, permissions: [...grantable] }));
  const clearAll = () => setForm((f) => ({ ...f, permissions: [] }));

  const save = async () => {
    const isGoogle = form.auth_provider === "google" || form.auth_provider === "local+google";
    if (!form.name || (!isGoogle && !form.username.trim()) || (!form.id && !form.password)) return toast.error(t("Fill all required fields"));
    const needsBranch = form.scope_type === "branch" || form.role === "BRANCH_STAFF";
    if (needsBranch && !form.branch_id) return toast.error(t("Select branch..."));
    setSaving(true);
    try {
      const payload = {
        name: form.name, username: form.username.trim(), role: form.role,
        branch_id: needsBranch ? form.branch_id : null,
        scope_type: needsBranch ? "branch" : "all",
        permissions: form.permissions,
        template: form.template || null,
        email: form.email ? form.email.trim() : null,
      };
      if (form.approving) payload.active = true;
      if (form.password) payload.password = form.password;
      if (form.id) {
        await api.put(`/users/${form.id}`, payload);
        toast.success(form.approving ? "Pengguna Google disetujui" : t("User updated"));
        if (form.id === me?.id) refreshPermissions();
      } else {
        await api.post("/users", payload);
        toast.success(t("User created"));
      }
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    setSaving(false);
  };

  const toggleActive = async (u) => { await api.put(`/users/${u.id}`, { active: !u.active }); load(); };
  const remove = async (u) => {
    if (!window.confirm(t("Delete this user?"))) return;
    try { await api.delete(`/users/${u.id}`); toast.success(t("User deleted")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  const openHistory = async (u) => {
    const { data } = await api.get(`/audit-logs?entity_id=${u.id}&limit=100`);
    const logs = data.filter((l) => ["CREATE_USER", "UPDATE_USER", "PERMISSIONS_UPDATED", "DELETE_USER"].includes(l.action));
    setHistory({ user: u, logs });
  };

  const templateOptions = (catalog.templates || []).filter((tpl) => assignable.includes(tpl.role));

  return (
    <div className="animate-fade-up">
      <PageHeader title={t("Users & Roles")} subtitle={t("Manage platform access")} icon={UsersIcon}>
        {hasPermission("create_users") && (
          <Button onClick={openCreate} data-testid="add-user"><Plus size={18} /> {t("Add User")}</Button>
        )}
      </PageHeader>

      <div className="mb-4 flex gap-2">
        <button onClick={() => setTab("active")} data-testid="tab-active"
          className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "active" ? "bg-primary text-white" : "border border-line text-ink-soft hover:bg-stone-50"}`}>
          {t("Users & Roles")}
        </button>
        <button onClick={() => setTab("pending")} data-testid="tab-pending"
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "pending" ? "bg-primary text-white" : "border border-line text-ink-soft hover:bg-stone-50"}`}>
          Menunggu Persetujuan
          {pendingUsers.length > 0 && (
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${tab === "pending" ? "bg-white/25 text-white" : "bg-amber-100 text-amber-700"}`} data-testid="pending-count">{pendingUsers.length}</span>
          )}
        </button>
      </div>

      {tab === "active" && (
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
              <th className="px-5 py-3">{t("Name")}</th>
              <th className="px-5 py-3">{t("Role")}</th>
              <th className="px-5 py-3">{t("Scope")}</th>
              <th className="px-5 py-3">{t("Last Login")}</th>
              <th className="px-5 py-3 text-center">{t("Status")}</th>
              <th className="px-5 py-3 text-right">{t("Actions")}</th>
            </tr></thead>
            <tbody>
              {activeUsers.map((u) => (
                <tr key={u.id} className="border-b border-line/60" data-testid={`user-row-${u.username || u.id}`}>
                  <td className="px-5 py-3">
                    <p className="font-semibold text-ink">{u.name}</p>
                    <p className="text-xs text-ink-mute">@{u.username || "—"}{u.email ? ` · ${u.email}` : ""}</p>
                  </td>
                  <td className="px-5 py-3">
                    <Badge tone="info">{t(ROLE_LABELS[u.role] || u.role)}</Badge>
                    {Array.isArray(u.permissions)
                      ? <span className="ml-1.5 inline-flex"><Badge tone="warn">{t("Custom")}</Badge></span>
                      : null}
                  </td>
                  <td className="px-5 py-3 text-ink-soft">
                    {u.effective_scope_type === "branch"
                      ? (u.branch_id ? branchName(u.branch_id) : t("Specific Branch"))
                      : t("All Branches")}
                  </td>
                  <td className="px-5 py-3 text-xs text-ink-mute">{u.last_login_at ? u.last_login_at.slice(0, 16).replace("T", " ") : t("Never")}</td>
                  <td className="px-5 py-3 text-center">
                    <button onClick={() => toggleActive(u)} disabled={!hasPermission("edit_users") || !canManage(u) || u.id === me?.id} data-testid={`toggle-${u.username || u.id}`}>
                      <Badge tone={u.active ? "success" : "mute"}><Circle size={8} className={u.active ? "fill-success" : "fill-stone-400"} /> {u.active ? t("Active") : t("Inactive")}</Badge>
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => openHistory(u)} className="rounded-lg p-1.5 text-ink-mute hover:bg-stone-100" title={t("Permission History")} data-testid={`history-${u.username || u.id}`}>
                        <History size={16} />
                      </button>
                      {hasPermission("edit_users") && canManage(u) && (
                        <button onClick={() => openEdit(u)} className="rounded-lg p-1.5 text-primary hover:bg-primary-light" title={t("Edit")} data-testid={`edit-${u.username || u.id}`}>
                          <Pencil size={16} />
                        </button>
                      )}
                      {hasPermission("delete_users") && canManage(u) && u.id !== me?.id && (
                        <button onClick={() => remove(u)} className="rounded-lg p-1.5 text-danger-text hover:bg-danger-light" title={t("Delete")} data-testid={`delete-${u.username || u.id}`}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      )}

      {tab === "pending" && (
        <Card className="overflow-hidden p-0" data-testid="pending-users-card">
          {pendingUsers.length === 0 ? (
            <p className="py-12 text-center text-sm text-ink-mute" data-testid="pending-empty">Tidak ada pengguna Google yang menunggu persetujuan.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                  <th className="px-5 py-3">{t("Name")}</th>
                  <th className="px-5 py-3">Email Google</th>
                  <th className="px-5 py-3">Tanggal Daftar</th>
                  <th className="px-5 py-3 text-center">{t("Status")}</th>
                  <th className="px-5 py-3 text-right">{t("Actions")}</th>
                </tr></thead>
                <tbody>
                  {pendingUsers.map((u) => (
                    <tr key={u.id} className="border-b border-line/60" data-testid={`pending-row-${u.id}`}>
                      <td className="px-5 py-3"><p className="font-semibold text-ink">{u.name}</p></td>
                      <td className="px-5 py-3 text-ink-soft">{u.google_email || u.email || "—"}</td>
                      <td className="px-5 py-3 text-xs text-ink-mute">{(u.created_at || "").slice(0, 10)}</td>
                      <td className="px-5 py-3 text-center"><Badge tone="warn">Menunggu</Badge></td>
                      <td className="px-5 py-3 text-right">
                        {hasPermission("edit_users") && (
                          <Button onClick={() => openApprove(u)} data-testid={`approve-${u.id}`}><ShieldCheck size={16} /> Setujui</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {open && (
        <UserFormModal
          form={form} setForm={setForm} catalog={catalog} branches={branches} grantable={grantable}
          templateOptions={templateOptions} assignable={assignable} lang={lang} t={t} saving={saving}
          onClose={() => { setOpen(false); setForm(EMPTY); }} onSave={save}
          applyTemplate={applyTemplate} togglePerm={togglePerm} toggleModule={toggleModule}
          selectAll={selectAll} clearAll={clearAll}
        />
      )}

      {history && (
        <Modal open onClose={() => setHistory(null)} title={`${t("Permission History")} · ${history.user.name}`}>
          {history.logs.length === 0 ? (
            <p className="py-8 text-center text-sm text-ink-mute" data-testid="history-empty">{t("No permission changes yet")}</p>
          ) : (
            <div className="space-y-3" data-testid="history-list">
              {history.logs.map((l) => (
                <div key={l.id} className="flex gap-3 rounded-xl border border-line p-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary"><ShieldCheck size={15} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-primary">{l.action.replace(/_/g, " ")}</span>
                      <span className="text-xs text-ink-mute">{t("by")} {l.user_name}</span>
                    </div>
                    {l.detail && <p className="break-words text-xs text-ink-soft">{l.detail}</p>}
                    <p className="text-[11px] text-ink-mute">{(l.created_at || "").slice(0, 19).replace("T", " ")}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function UserFormModal({ form, setForm, catalog, branches, grantable, templateOptions, assignable, lang, t, saving, onClose, onSave, applyTemplate, togglePerm, toggleModule, selectAll, clearAll }) {
  const needsBranch = form.scope_type === "branch" || form.role === "BRANCH_STAFF";
  const grantableCount = grantable.size;
  const selectedCount = useMemo(() => form.permissions.filter((p) => grantable.has(p)).length, [form.permissions, grantable]);

  return (
    <Modal open onClose={onClose} size="xl" title={form.approving ? "Setujui Pengguna Google" : (form.id ? t("Edit User") : t("New User"))}
      footer={<>
        <Button variant="outline" onClick={onClose}>{t("Cancel")}</Button>
        <Button onClick={onSave} loading={saving} data-testid="save-user">{form.id ? t("Save") : t("Create")}</Button>
      </>}>
      <div className="grid gap-5 lg:grid-cols-2">
        {/* Left: account details */}
        <div className="space-y-3">
          <div><label className="cf-label">{t("Name")}</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cf-input" data-testid="uf-name" /></div>
          <div><label className="cf-label">{t("Username")}</label>
            <input value={form.username} autoCapitalize="none" autoCorrect="off" onChange={(e) => setForm({ ...form, username: e.target.value })} className="cf-input" placeholder={t("Used to sign in")} data-testid="uf-username" /></div>
          <div><label className="cf-label">{t("Email")} <span className="font-normal text-ink-mute">({t("Optional")})</span></label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="cf-input" data-testid="uf-email" /></div>
          <div><label className="cf-label">{t("Password")}</label>
            <input type="text" value={form.password} placeholder={form.id ? t("Leave blank to keep current password") : ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className="cf-input" data-testid="uf-password" /></div>

          <Select label={t("Role Template")} value={form.template} onChange={(e) => applyTemplate(e.target.value)} data-testid="uf-template">
            <option value="">{t("Choose a template...")}</option>
            {[...new Set(templateOptions.map((tp) => tp.group || "Lainnya"))].map((grp) => (
              <optgroup key={grp} label={grp}>
                {templateOptions.filter((tp) => (tp.group || "Lainnya") === grp).map((tp) => (
                  <option key={tp.id} value={tp.id}>{tp.label}</option>
                ))}
              </optgroup>
            ))}
          </Select>
          <p className="-mt-1 text-xs text-ink-mute">{t("Applying a template pre-fills permissions below")}</p>

          <Select label={t("Role")} value={form.role} onChange={(e) => { const role = e.target.value; setForm({ ...form, role, template: "", scope_type: role === "BRANCH_STAFF" ? "branch" : form.scope_type }); }} data-testid="uf-role">
            {assignable.map((r) => <option key={r} value={r}>{t(ROLE_LABELS[r] || r)}</option>)}
          </Select>

          {/* Scope */}
          <div>
            <label className="cf-label">{t("Access Scope")}</label>
            <div className="grid grid-cols-2 gap-2">
              {[["all", t("All Branches")], ["branch", t("Specific Branch")]].map(([val, label]) => (
                <button key={val} type="button" onClick={() => setForm({ ...form, scope_type: val })}
                  data-testid={`uf-scope-${val}`}
                  className={`rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${form.scope_type === val ? "border-primary bg-primary-light text-primary" : "border-line text-ink-soft hover:bg-stone-50"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {needsBranch && (
            <Select label={t("Branch")} value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} data-testid="uf-branch">
              <option value="">{t("Select branch...")}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          )}
        </div>

        {/* Right: permission matrix */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <label className="cf-label mb-0">{t("Permissions")}</label>
              <p className="text-xs text-ink-mute" data-testid="uf-perm-count">{t("Selected")}: {selectedCount}/{grantableCount}</p>
            </div>
            <div className="flex gap-1.5">
              <button type="button" onClick={selectAll} className="rounded-lg border border-line px-2.5 py-1 text-xs font-semibold text-ink-soft hover:bg-stone-50" data-testid="uf-select-all">{t("Select all")}</button>
              <button type="button" onClick={clearAll} className="rounded-lg border border-line px-2.5 py-1 text-xs font-semibold text-ink-soft hover:bg-stone-50" data-testid="uf-clear-all">{t("Clear all")}</button>
            </div>
          </div>

          <div className="max-h-[46vh] space-y-2.5 overflow-y-auto pr-1">
            {(catalog.modules || []).map((mod) => {
              const keys = mod.permissions.filter((p) => grantable.has(p));
              const allOn = keys.length > 0 && keys.every((p) => form.permissions.includes(p));
              const hasRestricted = mod.permissions.some((p) => !grantable.has(p));
              return (
                <div key={mod.key} className="rounded-xl border border-line p-3" data-testid={`perm-module-${mod.key}`}>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input type="checkbox" checked={allOn} onChange={() => toggleModule(mod)} disabled={keys.length === 0}
                      className="h-4 w-4 accent-primary" data-testid={`perm-module-toggle-${mod.key}`} />
                    <span className="text-sm font-bold text-ink">{moduleLabel(mod.key, lang)}</span>
                    {hasRestricted && <Badge tone="mute" className="text-[10px]">{t("Some permissions are restricted to Super Admin")}</Badge>}
                  </label>
                  <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                    {mod.permissions.map((p) => {
                      const disabled = !grantable.has(p);
                      return (
                        <label key={p} className={`flex items-center gap-2 rounded-lg px-2 py-1 text-sm ${disabled ? "opacity-40" : "cursor-pointer hover:bg-stone-50"}`}>
                          <input type="checkbox" checked={form.permissions.includes(p)} disabled={disabled}
                            onChange={() => togglePerm(p)} className="h-4 w-4 accent-primary" data-testid={`perm-${p}`} />
                          <span className="text-ink-soft">{permLabel(p, lang)}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}
